import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { Theme } from '@carbon/react/es/components/Theme';

import { useAuthStore } from './auth/store.js';
import { useThemeStore } from './theme/store.js';
import { AppLayout } from './routes/AppLayout.js';
import { LoginPage } from './routes/LoginPage.js';
import { RegisterPage } from './routes/RegisterPage.js';
import { OverviewPage } from './routes/OverviewPage.js';
import { WorkflowPage } from './routes/WorkflowPage.js';
import { VideosPage, DataVideosPage, DataImagesPage } from './routes/VideosPage.js';
import { VideoSearchPage } from './routes/VideoSearchPage.js';
import { ProjectDetailPage } from './routes/ProjectDetailPage.js';
import { VideoAnnotatePage } from './routes/VideoAnnotatePage.js';
import { ProfilePage } from './routes/ProfilePage.js';
import { MembersPage } from './routes/MembersPage.js';
import { AdminConsolePage } from './routes/AdminConsolePage.js';
import { AdminOrgDetailPage } from './routes/AdminOrgDetailPage.js';
import { AdminOrgPage } from './routes/AdminOrgPage.js';
import { ModelsPage } from './routes/ModelsPage.js';
import { DeployPage } from './routes/DeployPage.js';
import { OpsPage } from './routes/OpsPage.js';
import { CorbeillePage } from './routes/CorbeillePage.js';
import { SharePage } from './routes/SharePage.js';
import { AcceptInvitePage } from './routes/AcceptInvitePage.js';

const rootRoute = createRootRoute({
  component: RootShell,
});

/**
 * Coquille racine : le thème Carbon suit la préférence (nuit par défaut / jour). Les jetons visuels
 * viennent de l'attribut `data-carbon-theme` posé par le store (app.scss) ; on tient ici le contexte
 * React de Carbon en accord, pour les composants qui lisent le thème via `useTheme()`.
 */
function RootShell() {
  const mode = useThemeStore((state) => state.mode);
  return (
    <Theme theme={mode === 'light' ? 'white' : 'g100'}>
      <Outlet />
    </Theme>
  );
}

/** Une session vivante n'a rien à faire sur les pages publiques : on la renvoie à l'accueil. */
const redirectIfAuthenticated = () => {
  if (useAuthStore.getState().status === 'authenticated') {
    throw redirect({ to: '/' });
  }
};

/** Porte des pages authentifiées. La session étant en mémoire, un rechargement y ramène au login. */
const requireAuthenticated = () => {
  if (useAuthStore.getState().status !== 'authenticated') {
    throw redirect({ to: '/login' });
  }
};

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: redirectIfAuthenticated,
  component: LoginPage,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  beforeLoad: redirectIfAuthenticated,
  component: RegisterPage,
});

// Page publique d'un partage : hors de la porte d'authentification (un visiteur anonyme y accède)
// et hors de la redirection des connectés (un membre peut aussi ouvrir un lien qu'on lui a envoyé).
const shareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/partage/$token',
  component: SharePage,
});

// Acceptation d'invitation : page publique (l'invité n'a pas encore de compte). Le jeton arrive en
// paramètre de recherche (`?token=…`). Hors redirection des connectés : le lien reste ouvrable.
const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invitations/accept',
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: AcceptInvitePage,
});

// Route de disposition sans segment d'URL : elle porte la garde et l'en-tête communs.
const protectedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  beforeLoad: requireAuthenticated,
  component: AppLayout,
});

// « Overview » — tableau de bord récapitulatif (page d'accueil).
const indexRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/',
  component: OverviewPage,
});

// « Parcours guidé » — fil d'onboarding pas-à-pas (ADR-0020). Atteint depuis l'appel d'Overview,
// hors du rail à quatre zones qu'il ne remplace pas.
const workflowRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/parcours',
  component: WorkflowPage,
});

// « Data collection » — la bibliothèque : ajout, caractéristiques, organisation.
const dataRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/data',
  component: VideosPage,
});

// Pages dédiées « toutes les vidéos » / « toutes les images » : atteintes depuis les cartes
// « Autres vidéos / images » de la page Data, elles montrent la bibliothèque complète sans troncature.
const dataVideosRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/data/videos',
  component: DataVideosPage,
});

const dataImagesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/data/images',
  component: DataImagesPage,
});

// Recherche avancée (FR-19→22) : plein texte + filtres, atteinte depuis la page Vidéos.
const dataVideosSearchRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/data/videos/search',
  component: VideoSearchPage,
});

const videoAnnotateRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/videos/$videoId',
  component: VideoAnnotatePage,
});

// Détail d'un projet transverse (ADR-0024) : ses vidéos membres. Atteint depuis la section
// « Projets » de l'Overview. Le cloisonnement est côté API (404 sur un projet étranger).
const projectDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/projects/$projectId',
  component: ProjectDetailPage,
});

const profileRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/profil',
  component: ProfilePage,
});

// « Membres » — administration des personnes de l'organisation (volet A). Accessible à tout membre
// en lecture ; les actions d'écriture sont réservées aux administrateurs (gardées côté serveur).
const membersRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/organisation/membres',
  component: MembersPage,
});

// « Administration de l'organisation » — vue org-admin (ADR-0037) : état de l'architecture (puis
// usages & coûts). La porte d'authentification suffit côté route ; le rôle ADMIN est vérifié par
// l'API (404 sinon) et la page se masque pour un rôle insuffisant.
const adminOrgRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/organisation/admin',
  component: AdminOrgPage,
});

// « Administration » — console plateforme transverse (ADR-0025). La porte d'authentification suffit
// côté route : le rôle plateforme est vérifié par l'API (404 sinon) et la page se masque elle-même
// pour un compte non habilité.
const adminRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/admin',
  component: AdminConsolePage,
});

// Détail d'une organisation vue depuis l'administration (membres + consommation par utilisateur).
const adminOrgDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/admin/organisations/$organizationId',
  component: AdminOrgDetailPage,
});

// « Model » — annotation, évaluation, entraînement (registre de modèles).
const modelRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/model',
  component: ModelsPage,
});

// « Deploy » — déploiement sur le matériel (Phase D).
const deployRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/deploy',
  component: DeployPage,
});

// « Ops » — vue opérationnelle counter-UAS (ADR-0040). La porte d'authentification suffit côté route :
// l'autorisation reste portée par l'API (OrgRole VIEWER+) et le persona ne fait que curer la nav.
const opsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/ops',
  component: OpsPage,
});

const trashRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/corbeille',
  component: CorbeillePage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  registerRoute,
  shareRoute,
  acceptInviteRoute,
  protectedLayoutRoute.addChildren([
    indexRoute,
    workflowRoute,
    dataRoute,
    dataVideosRoute,
    dataVideosSearchRoute,
    dataImagesRoute,
    videoAnnotateRoute,
    projectDetailRoute,
    profileRoute,
    membersRoute,
    adminOrgRoute,
    adminRoute,
    adminOrgDetailRoute,
    modelRoute,
    deployRoute,
    opsRoute,
    trashRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
